import { useEffect, useState, useCallback } from "react";
import {
  Card,
  Row,
  Col,
  Tabs,
  Tab,
  Badge,
  // Dropdown,
  // ButtonGroup,
  Button,
  Modal,
  Alert
} from "react-bootstrap";
import axios from "axios";
import Layout from "../../components/layouts/Main";
import config from "../../config";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { PulseLoader } from "react-spinners";
// import { FaDownload, FaFilePdf, FaMoneyBillWave, FaCheckCircle, FaHourglassHalf } from "react-icons/fa";
import { useHasPermission, PERMISSIONS } from "../../utils/permissions";

// Tab Components
import SODetailsTab from "../../components/salesOrders/SODetailsTab";
import SODispatchTab from "../../components/salesOrders/SODispatchTab";
import SOPaymentTab from "../../components/salesOrders/SOPaymentTab";
import QualityTestingTab from "../../components/salesOrders/QualityTestingTab";
import ConfirmationModal from "../../components/common/ConfirmationModal";

export default function SalesOrderDetails() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [buyer, setBuyer] = useState(null);
  const [broker, setBroker] = useState(null);
  const [items, setItems] = useState([]);
  const [qcTests, setQcTests] = useState([]);

  // Check if tab parameter is in URL (e.g., ?tab=dispatch)
  const tabFromUrl = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState(tabFromUrl || "po-details");

  const [downloading, setDownloading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmConfig, setConfirmConfig] = useState({
    title: '',
    message: '',
    confirmVariant: 'primary',
    confirmText: 'Confirm',
    onConfirm: () => { }
  });

  const openConfirm = (config) => {
    setConfirmConfig(config);
    setShowConfirmModal(true);
  };

  const handleApprove = async () => {
    setSubmitting(true);
    try {
      await axios.patch(`${config.API_BASE_URL}/sales-orders/${order._id}/approve`);
      toast.success("Sales Order approved successfully");
      fetchOrder();
    } catch (err) {
      toast.error("Failed to approve Sales Order");
    } finally {
      setSubmitting(false);
      setShowConfirmModal(false);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    setSubmitting(true);
    try {
      await axios.patch(`${config.API_BASE_URL}/sales-orders/${order._id}/status`, { status: newStatus });
      toast.success(`Order status updated to ${newStatus}`);
      fetchOrder();
    } catch (err) {
      toast.error("Failed to update status");
    } finally {
      setSubmitting(false);
      setShowConfirmModal(false);
    }
  };

  useEffect(() => {
    if (order && !tabFromUrl) {
      if (order.status === 'Draft') {
        setActiveTab('draft-details');
      } else if (activeTab === 'draft-details') {
        setActiveTab('po-details');
      }
    }
  }, [order, tabFromUrl]);

  // Permission checks
  const canViewSO = useHasPermission(PERMISSIONS.SO_VIEW);
  const canEditSO = useHasPermission(PERMISSIONS.SO_EDIT);
  const canViewDispatch = useHasPermission(PERMISSIONS.DISPATCH_VIEW);
  const canViewPayment = useHasPermission(PERMISSIONS.SO_PAYMENT_VIEW);
  const canViewQC = useHasPermission(PERMISSIONS.SO_VIEW); // Using SO_VIEW for now, can add separate QC permission later

  const fetchOrder = useCallback(async () => {
    try {
      const res = await axios.get(`${config.API_BASE_URL}/sales-orders/${id}`);
      setOrder(res.data.order);
      setBuyer(res.data.buyer);
      setBroker(res.data?.broker);

      const itemsRes = await axios.get(
        `${config.API_BASE_URL}/sales-order-items?salesOrderId=${id}`
      );
      setItems(itemsRes.data);
    } catch (err) {
      toast.error("Error fetching sales order");
    }
  }, [id]);

  const fetchQCTests = useCallback(async () => {
    try {
      const response = await axios.get(`${config.API_BASE_URL}/qc-testing/${id}`);
      setQcTests(response.data);
    } catch (error) {
      console.error('Error fetching QC tests:', error);
    }
  }, [id]);

  useEffect(() => {
    fetchOrder();
    fetchQCTests();
  }, [id, fetchOrder, fetchQCTests]);

  const getStatusBadge = (status) => {
    const map = {
      Draft: "warning text-dark",
      Created: "secondary",
      "Stock Pending": "warning",
      "Production Requested": "info",
      "In Production": "info",
      "Filled From Production": "info",
      "Filled From Production & QC Pending": "warning",
      "Filled From Production & Ready for Dispatch": "primary",
      "Awaiting Receipt of PO": "warning",
      "Awaiting Receipt of PO (RM)": "warning",
      "Awaiting Receipt of PO (FG)": "warning",
      "PO Received": "info",
      "QC Pending": "warning",
      "Ready for Dispatch": "primary",
      "Partially Dispatched": "warning",
      "Fully Dispatched": "info",
      "In Transit": "warning",
      "Partially Delivered": "info",
      Delivered: "success",
      Completed: "success",
      "QC Failed": "danger",
      Cancelled: "danger",
      "On Hold": "danger"
    };
    return <Badge bg={map[status] || "dark"}>{status}</Badge>;
  };

  const getPaymentStatusBadge = (status) => {
    const map = {
      Pending: "warning",
      Partial: "info",
      Paid: "success",
      Cancelled: "danger"
    };
    return <Badge bg={map[status] || "secondary"}>{status}</Badge>;
  };

  // Download SO PDF
  const handleDownloadPDF = async () => {
    try {
      setDownloading(true);
      const res = await axios.get(
        `${config.API_BASE_URL}/sales-orders/${id}/download-pdf`,
        { responseType: "blob" }
      );

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `SO_${order.orderNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success("Sales Order PDF downloaded");
    } catch (err) {
      toast.error("Error downloading PDF");
      console.error(err);
    } finally {
      setDownloading(false);
    }
  };

  // Check if lab tests have passed (any test result is 'Pass' or any item has 'Passed' status)
  const hasLabTestPassed = () => {
    if (!qcTests || qcTests.length === 0) return false;

    // Check if any QC test has result = 'Pass'
    const hasPassedResult = qcTests.some(test => test.result === 'Pass');

    // Check if any item in any test has qcStatus = 'Passed'
    const hasPassedItem = qcTests.some(test =>
      test.itemWiseQCStatus?.some(item => item.qcStatus === 'Passed')
    );

    return hasPassedResult || hasPassedItem;
  };

  // Get overall QC status for updating sales order
  const getOverallQCStatus = () => {
    if (!qcTests || qcTests.length === 0) return null;

    // Check for any 'Fail' result
    const hasFailedResult = qcTests.some(test => test.result === 'Fail');
    if (hasFailedResult) return 'QC Failed';

    // Check for any 'Pass' result
    const hasPassedResult = qcTests.some(test => test.result === 'Pass');
    if (hasPassedResult) return 'Ready for Dispatch';

    // Check for 'Sample Collected'
    const hasSampleCollected = qcTests.some(test => test.result === 'Sample Collected');
    if (hasSampleCollected) return 'QC Pending';

    // Default to 'QC Pending' for 'Pending' status
    return 'QC Pending';
  };

  // Update sales order status based on QC changes
  const updateSalesOrderStatusFromQC = async () => {
    const qcStatus = getOverallQCStatus();
    if (!qcStatus) return;

    let newStatus;

    // Handle combined statuses (Filled From Production + QC status)
    if (order.status === "Filled From Production & QC Pending") {
      if (qcStatus === "Ready for Dispatch") {
        newStatus = "Filled From Production & Ready for Dispatch";
      } else if (qcStatus === "QC Failed") {
        newStatus = "Filled From Production & QC Pending"; // Keep as pending, or could use QC Failed if that combined status existed
      } else {
        newStatus = order.status; // No change needed
      }
    } else if (order.status === "Filled From Production & Ready for Dispatch") {
      if (qcStatus === "QC Failed") {
        newStatus = "Filled From Production & QC Pending";
      } else {
        newStatus = order.status; // No change needed
      }
    } else {
      // Handle simple statuses
      newStatus = qcStatus;
    }

    if (!newStatus || newStatus === order.status) return;

    try {
      await axios.patch(`${config.API_BASE_URL}/sales-orders/${order._id}/status`, {
        status: newStatus,
      });
      toast.success(`Order status updated to ${newStatus} due to QC changes`);
      fetchOrder();
    } catch (err) {
      toast.error("Failed to update order status");
      console.error(err);
    }
  };

  // const statuses = [
  //   "Created",
  //   "Stock Pending",
  //   "Production Requested",
  //   "In Production",
  //   "Filled From Production",
  //   "Filled From Production & QC Pending",
  //   "Filled From Production & Ready for Dispatch",
  //   "Awaiting Receipt of PO",
  //   "PO Received",
  //   "QC Pending",
  //   "Ready for Dispatch",
  //   "Partially Dispatched",
  //   "Fully Dispatched",
  //   "In Transit",
  //   "Partially Delivered",
  //   "Delivered",
  //   "Completed",
  //   "QC Failed",
  //   "Cancelled"
  // ];

  if (!order)
    return (
      <Layout>
        <div className="text-center py-4">
          <PulseLoader color="#014b7cbb" size={9} />
        </div>
      </Layout>
    );

  // If user can't view sales orders, show permission denied
  if (!canViewSO) {
    return (
      <Layout>
        <nav aria-label="breadcrumb" className="mb-4">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/sales-orders">Sales Orders</Link>
            </li>
            <li className="breadcrumb-item active">Access Denied</li>
          </ol>
        </nav>
        <Alert variant="danger" className="d-flex align-items-center">
          <i className="fas fa-exclamation-triangle me-2"></i>
          <div>
            <strong>Access Denied</strong>
            <p className="mb-0">You don't have permission to view sales order details. Contact your administrator for access.</p>
          </div>
        </Alert>
      </Layout>
    );
  }

  return (
    <Layout>
      <nav aria-label="breadcrumb" className="mb-4">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <Link to="/sales-orders">Sales Orders</Link>
          </li>
          <li className="breadcrumb-item active">{order.orderNumber ? `#${order.orderNumber}` : 'Draft SO'}</li>
        </ol>
      </nav>

      <Row className="mb-4 gy-3">
        <Col className="col">
          <h2>
            {order.orderNumber ? `Order #${order.orderNumber}` : 'Draft Sales Order'}
            {order.isInHouseOrder && (
              <span className="badge bg-success ms-2">
                <i className="fas fa-building me-1"></i>In-House
              </span>
            )}
          </h2>
        </Col>
        <Col className="text-end col-auto">
          <div className="d-flex justify-content-end flex-wrap gap-2">
            {canEditSO && order.status === 'Draft' && (
              <>
                <Button
                    variant="primary"
                    onClick={() => openConfirm({
                    title: 'Approve Sales Order',
                    message: 'Are you sure you want to approve this draft Sales Order? This will generate a Sales Order number.',
                    confirmVariant: 'success',
                    confirmText: 'Approve',
                    onConfirm: handleApprove
                    })}
                >
                    <i className="bi bi-check-circle me-1"></i> Approve SO
                </Button>
                <Button
                    variant="danger"
                    onClick={() => openConfirm({
                        title: 'Reject Sales Order',
                        message: 'Are you sure you want to reject/cancel this draft Sales Order?',
                        confirmVariant: 'danger',
                        confirmText: 'Reject Order',
                        onConfirm: () => handleStatusUpdate('Cancelled')
                    })}
                >
                    <i className="bi bi-x-circle me-1"></i> Reject/Cancel
                </Button>
              </>
            )}
            {canEditSO && order.status !== 'Draft' && !['Delivered', 'Cancelled', 'Completed', 'Fully Dispatched'].includes(order.status) && (
                 <Button
                    variant="outline-danger"
                    size="sm"
                    className="me-2"
                    onClick={() => openConfirm({
                        title: 'Cancel Sales Order',
                        message: 'Are you sure you want to cancel this Sales Order?',
                        confirmVariant: 'danger',
                        confirmText: 'Cancel Order',
                        onConfirm: () => handleStatusUpdate('Cancelled')
                    })}
                >
                    <i className="bi bi-x-circle me-1"></i> Cancel Order
                </Button>
            )}
            {canEditSO && (
              order.status === 'On Hold' ? (
                <Button
                  variant="success"
                  onClick={() => openConfirm({
                    title: 'Unhold Sales Order',
                    message: 'Are you sure you want to resume this Sales Order?',
                    confirmVariant: 'success',
                    confirmText: 'Resume Order',
                    onConfirm: () => handleStatusUpdate('Created')
                  })}
                >
                  <i className="bi bi-play-circle me-1"></i> Unhold Order
                </Button>
              ) : (
                !['Draft', 'Delivered', 'Cancelled', 'Completed', 'Fully Dispatched'].includes(order.status) && (
                  <Button
                    variant="danger"
                    onClick={() => openConfirm({
                      title: 'Hold Sales Order',
                      message: 'Are you sure you want to put this Sales Order on hold?',
                      confirmVariant: 'warning',
                      confirmText: 'Hold Order',
                      onConfirm: () => handleStatusUpdate('On Hold')
                    })}
                  >
                    <i className="bi bi-pause-circle me-1"></i> Put on Hold
                  </Button>
                )
              )
            )}
            {canViewSO && order.orderNumber && (
              <Button
                variant="success"
                className="me-2"
                onClick={handleDownloadPDF}
                disabled={downloading}
              >
                {downloading ? "Downloading..." : 'Download SO PDF'}
                {/* <FaDownload className="me-1" /> Download SO PDF */}
              </Button>
            )}
            {canEditSO && (
              <Link
                className="btn btn-outline-secondary me-2"
                to={`/edit-sales-order/${order._id}`}
              >
                <i className="bi bi-pencil-square me-1"></i> Edit
              </Link>
            )}
          </div>
        </Col>
      </Row>

      <Row className="mb-4">
        {/* Top Summary Card */}
        <Card className="mb-4 shadow border-0">
          <Card.Body>
            <Row className="align-items-center">
              <Col md={3} className="mt-0">
                <div className="d-flex flex-column">
                  <strong>SO Status:</strong>
                  <div className="d-flex align-items-center gap-2">
                    {getStatusBadge(order.status)}
                    {/* {canEditSO && (
                      <Dropdown as={ButtonGroup}>
                        <Dropdown.Toggle
                          size="sm"
                          variant="outline-primary"
                          id="dropdown-status"
                        >
                          Change
                        </Dropdown.Toggle>

                        <Dropdown.Menu>
                          {statuses.map((s) => (
                            <Dropdown.Item
                              key={s}
                              onClick={() => openConfirmModal(s)}
                              disabled={s === order.status}
                            >
                              {s}
                            </Dropdown.Item>
                          ))}
                        </Dropdown.Menu>
                      </Dropdown>
                    )} */}
                  </div>
                </div>
              </Col>

              {canViewPayment && (
                <Col md={3} className="mt-3 mt-md-0">
                  <div className="d-flex flex-column">
                    <strong>Payment Status:</strong>
                    <div className="d-flex align-items-center gap-2">
                      {getPaymentStatusBadge(order.paymentStatus || 'Pending')}
                      {/* {order.totalAmountPaid && order.totalAmount && (
                        <small className="text-muted">
                          ₹{order.totalAmountPaid?.toLocaleString() || 0} / ₹{order.totalAmount?.toLocaleString() || 0}
                        </small>
                      )} */}
                    </div>
                  </div>
                </Col>
              )}

              <Col md={3} className="mt-3 mt-md-0">
                <div className="text-dark d-flex flex-column">
                  <strong>Buyer</strong>{" "}
                  <span>{buyer?.business_name || "—"}</span>
                </div>
              </Col>

              <Col md={3} className="mt-3 mt-md-0">
                <div className="text-dark d-flex flex-column">
                  {broker && (
                    <>
                      <strong>via:</strong>
                      <span>{broker?.business_name}</span>
                    </>
                  )}
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Tab View */}
        <Card className="shadow-sm border-0">
          <Card.Body>
            <Tabs
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k)}
              className="d-flex flex-nowrap mb-4 border-bottom"
              justify
            >
              {order.status === 'Draft' ? (
                <Tab eventKey="draft-details" title="Draft SO Details">
                  <SODetailsTab order={order} items={items} onChange={fetchOrder} />
                  <div className="text-center p-5 border rounded bg-light mt-4">
                    <h3>Draft Sales Order</h3>
                    <p className="text-muted">This order is currently in draft status and has no order number. Approve it to generate an order number and start production/dispatch.</p>
                    <Button variant="primary" size="lg" onClick={() => openConfirm({
                      title: 'Approve Sales Order',
                      message: 'Are you sure you want to approve this draft Sales Order? This will generate a Sales Order number.',
                      confirmVariant: 'success',
                      confirmText: 'Approve',
                      onConfirm: handleApprove
                    })}>
                      <i className="bi bi-check-circle me-1"></i> Approve Sales Order
                    </Button>
                  </div>
                </Tab>
              ) : (
                <Tab eventKey="po-details" title="Order Details">
                  <SODetailsTab order={order} items={items} onChange={fetchOrder} />
                </Tab>
              )}

              {canViewQC && (
                order.status === 'Draft' ? (
                  <Tab eventKey="qc-testing" title="QC & Lab Testing" disabled className="text-muted">
                    <Alert variant="warning" className="mt-3">
                      <i className="fas fa-lock me-2"></i>
                      <strong>QC Locked</strong>
                      <p className="mb-0">Approve the draft order to enable QC.</p>
                    </Alert>
                  </Tab>
                ) : order.status === 'Cancelled' ? (
                  <Tab eventKey="qc-testing" title="QC & Lab Testing" disabled className="text-muted">
                    <Alert variant="danger" className="mt-3">
                      <i className="fas fa-lock me-2"></i>
                      <strong>SO Cancelled</strong>
                    </Alert>
                  </Tab>
                ) : order.labTestRequired === false ? (
                  <Tab
                    eventKey="qc-testing"
                    title="QC & Lab Testing"
                    disabled
                    className="text-muted"
                  >
                    <Alert variant="warning" className="mt-3">
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      <strong>QC Tab Disabled</strong>
                      <p className="mb-0">Lab testing is not required for this order.</p>
                    </Alert>
                  </Tab>
                ) : (
                  <Tab eventKey="qc-testing" title="QC & Lab Testing">
                    <QualityTestingTab
                      salesOrderId={order._id || ''}
                      salesOrderItems={items}
                      onQCChange={updateSalesOrderStatusFromQC}
                    />
                  </Tab>
                )
              )}

              {canViewDispatch && (
                order.status === 'Draft' ? (
                  <Tab eventKey="dispatch" title="Dispatch & Delivery" disabled className="text-muted">
                    <Alert variant="warning" className="mt-3">
                      <i className="fas fa-lock me-2"></i>
                      <strong>Dispatch Locked</strong>
                      <p className="mb-0">Approve the draft order to enable dispatch.</p>
                    </Alert>
                  </Tab>
                ) : order.status === 'Cancelled' ? (
                  <Tab eventKey="dispatch" title="Dispatch & Delivery" disabled className="text-muted">
                    <Alert variant="danger" className="mt-3">
                      <i className="fas fa-lock me-2"></i>
                      <strong>SO Cancelled</strong>
                    </Alert>
                  </Tab>
                ) : order.status === "On Hold" ? (
                  <Tab
                    eventKey="dispatch"
                    title="Dispatch & Delivery"
                    disabled
                    className="text-muted"
                  >
                    <Alert variant="danger" className="mt-3">
                      <i className="fas fa-pause-circle me-2"></i>
                      <strong>Order On Hold</strong>
                      <p className="mb-0">This order is currently on hold. Resume the order to proceed with dispatch.</p>
                    </Alert>
                  </Tab>
                ) : order.labTestRequired && !hasLabTestPassed() ? (
                  <Tab
                    eventKey="dispatch"
                    title="Dispatch & Delivery"
                    disabled
                    className="text-muted"
                  >
                    <Alert variant="warning" className="mt-3">
                      <i className="fas fa-lock me-2"></i>
                      <strong>Dispatch Tab Locked</strong>
                      <p className="mb-0">Complete lab testing before proceeding with dispatch.</p>
                    </Alert>
                  </Tab>
                ) : (
                  <Tab eventKey="dispatch" title="Dispatch & Delivery">
                    <SODispatchTab
                      orderId={order._id || ''}
                      orderData={order}
                      items={items}
                      onRefresh={fetchOrder}
                    />
                  </Tab>
                )
              )}

              {canViewPayment && (
                order.status === 'Draft' ? (
                  <Tab eventKey="payment" title="Payment" disabled className="text-muted">
                    <Alert variant="warning" className="mt-3">
                      <i className="fas fa-lock me-2"></i>
                      <strong>Payment Locked</strong>
                      <p className="mb-0">Approve the draft order to enable payment.</p>
                    </Alert>
                  </Tab>
                ) : order.status === 'Cancelled' ? (
                  <Tab eventKey="payment" title="Payment" disabled className="text-muted">
                    <Alert variant="danger" className="mt-3">
                      <i className="fas fa-lock me-2"></i>
                      <strong>SO Cancelled</strong>
                    </Alert>
                  </Tab>
                ) : order.status === "On Hold" ? (
                  <Tab
                    eventKey="payment"
                    title="Payment"
                    disabled
                    className="text-muted"
                  >
                    <Alert variant="danger" className="mt-3">
                      <i className="fas fa-pause-circle me-2"></i>
                      <strong>Order On Hold</strong>
                      <p className="mb-0">This order is currently on hold. Resume the order to proceed with payment.</p>
                    </Alert>
                  </Tab>
                ) : order.labTestRequired && !hasLabTestPassed() ? (
                  <Tab
                    eventKey="payment"
                    title="Payment"
                    disabled
                    className="text-muted"
                  >
                    <Alert variant="warning" className="mt-3">
                      <i className="fas fa-lock me-2"></i>
                      <strong>Payment Tab Locked</strong>
                      <p className="mb-0">Complete lab testing before proceeding with payment.</p>
                    </Alert>
                  </Tab>
                ) : (
                  <Tab eventKey="payment" title="Payment">
                    <SOPaymentTab
                      orderId={order._id || ''}
                      orderData={order}
                      onRefresh={fetchOrder}
                    />
                  </Tab>
                )
              )}
            </Tabs>
          </Card.Body>
        </Card>
      </Row>

      <ConfirmationModal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        onConfirm={confirmConfig.onConfirm}
        title={confirmConfig.title}
        message={confirmConfig.message}
        confirmText={confirmConfig.confirmText}
        confirmVariant={confirmConfig.confirmVariant}
        isLoading={submitting}
      />
    </Layout>


  );
}
